import ScrollReveal from "@/components/base/ScrollReveal";
import { currentGroupOfferings } from "@/data/groupOfferings.js";

interface CurrentGroupOfferingsProps {
  heading?: string;
  eyebrow?: string;
}

export default function CurrentGroupOfferings({
  heading = "Current Fall 2026 child groups",
  eyebrow = "Now Registering",
}: CurrentGroupOfferingsProps) {
  return (
    <section id="current-groups" className="w-full bg-[#F8F5EE] py-20 md:py-28">
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className="mb-12 max-w-3xl">
          <ScrollReveal variant="fadeIn" delay={0}>
            <span className="mb-4 block font-manrope text-[13px] font-semibold uppercase tracking-widest text-[#6B742F]">
              {eyebrow}
            </span>
          </ScrollReveal>
          <ScrollReveal variant="fadeUp" delay={80}>
            <h2 className="font-fraunces text-3xl font-light leading-snug text-[#2B2B2B] md:text-4xl">
              {heading}
            </h2>
          </ScrollReveal>
          <ScrollReveal variant="fadeUp" delay={140}>
            <p className="mt-5 font-manrope text-[15px] leading-relaxed text-[#2B2B2B]/78">
              Two six-week, play-based groups for children ages 6–7 are open for
              Fall 2026 registration at our Larchmont office.
            </p>
          </ScrollReveal>
        </div>

        <div className="grid grid-cols-1 gap-7 xl:grid-cols-2">
          {currentGroupOfferings.map((group, index) => (
            <ScrollReveal key={group.id} variant="fadeUp" delay={index * 90}>
              <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-[#CBBFAF]/40 bg-white/90">
                <div className="h-[3px] bg-gradient-to-r from-[#7D54FC] to-[#3C6FF8]" />
                <div className="flex flex-1 flex-col p-6 md:p-8">
                  <span className="w-fit rounded-full bg-[#6B742F]/10 px-3 py-1 font-manrope text-xs font-semibold text-[#6B742F]">
                    {group.registrationStatus}
                  </span>
                  <h3 className="mt-5 font-fraunces text-2xl font-medium text-[#2B2B2B] md:text-3xl">
                    {group.name}
                  </h3>
                  <p className="mt-1 font-manrope text-sm font-semibold text-[#7D54FC]">
                    {group.subtitle}
                  </p>
                  <p className="mt-4 font-manrope text-[15px] leading-relaxed text-[#2B2B2B]/78">
                    {group.clinicalFocus}
                  </p>

                  <dl className="mt-6 grid grid-cols-1 gap-4 rounded-xl bg-[#F8F5EE] p-5 sm:grid-cols-2">
                    {[
                      ["Age", group.ageRange],
                      ["Schedule", `${group.meetingDay}, ${group.meetingTime}`],
                      ["Program", `${group.programLength}, ${group.frequency.toLowerCase()}`],
                      ["Format", `${group.format}, Larchmont`],
                      ["Facilitators", group.facilitators.join(" + ")],
                      ["Cohort fee", group.price],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt className="font-manrope text-xs font-semibold uppercase tracking-wide text-[#2B2B2B]/50">
                          {label}
                        </dt>
                        <dd className="mt-1 font-manrope text-sm leading-relaxed text-[#2B2B2B]">
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>

                  <div className="mt-6 grid gap-6 md:grid-cols-2">
                    <div>
                      <h4 className="font-fraunces text-lg font-medium text-[#2B2B2B]">
                        This group may help with
                      </h4>
                      <ul className="mt-3 space-y-2">
                        {group.fit.map((item) => (
                          <li key={item} className="flex gap-2 font-manrope text-sm leading-relaxed text-[#2B2B2B]/75">
                            <i className="ri-check-line mt-0.5 text-[#7D54FC]" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-fraunces text-lg font-medium text-[#2B2B2B]">
                        Children will practice
                      </h4>
                      <ul className="mt-3 space-y-2">
                        {group.skills.map((item) => (
                          <li key={item} className="flex gap-2 font-manrope text-sm leading-relaxed text-[#2B2B2B]/75">
                            <i className="ri-check-line mt-0.5 text-[#3C6FF8]" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-7 border-t border-[#2B2B2B]/10 pt-6">
                    <h4 className="font-fraunces text-lg font-medium text-[#2B2B2B]">
                      Enrollment and thoughtful matching
                    </h4>
                    <p className="mt-3 font-manrope text-sm leading-relaxed text-[#2B2B2B]/75">
                      {group.screening} {group.placement.join(" ")}
                    </p>
                    <p className="mt-3 font-manrope text-sm leading-relaxed text-[#2B2B2B]/75">
                      Enrollment includes {group.enrollmentIncludes.join(", ")}.
                    </p>
                    <p className="mt-3 font-manrope text-sm leading-relaxed text-[#2B2B2B]/75">
                      {group.startTiming}. Sessions meet at {group.location.name},{" "}
                      {group.location.street}, {group.location.city}. {group.paymentPositioning}
                    </p>
                  </div>

                  <div className="mt-auto pt-7">
                    <a
                      href={group.registrationPath}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3C6FF8] px-6 py-3 font-manrope text-[15px] font-semibold text-white transition-colors hover:bg-[#2a5de6]"
                    >
                      Register for This Group <i className="ri-arrow-right-line" />
                    </a>
                    <p className="mt-3 font-manrope text-xs leading-relaxed text-[#2B2B2B]/60">
                      Registration begins the enrollment process. Placement is
                      confirmed after the caregiver intake and group-fit screening.
                    </p>
                  </div>
                </div>
              </article>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
